import { ChangeEvent, Dispatch, SetStateAction, useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { Modal, Button, CardMedia, Input, FormHelperText } from "@mui/material";
import { useUploadFileMutation } from "../store/api/commonAPI";
import { useCreateCategoryMutation, useUpdateCategoryMutation, CategoryItem } from "../store/api/manageAPI";
import { showToast, showError } from "../constants/toasts";
import Loader from "../constants/Loader";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  editCategory?: CategoryItem | null;
}

export default function AddProductCategory({
  open,
  onClose,
  setOpen,
  editCategory,
}: ModalProps) {
  const [image, setImage] = useState<string>("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [errors, setErrors] = useState<{ name?: string; image?: string }>({});

  const isEditMode = !!editCategory;

  // Upload file and create/update category mutations
  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();
  const [createCategory, { isLoading: isCreating }] = useCreateCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] = useUpdateCategoryMutation();

  const isLoading = isUploading || isCreating || isUpdating;

  // Reset form when modal opens/closes or when editCategory changes
  useEffect(() => {
    if (!open) {
      setImage("");
      setImageFile(null);
      setUploadedImageUrl("");
      setFileName("");
      setName("");
      setErrors({});
    } else if (editCategory) {
      // Populate form with edit data
      setName(editCategory.name || "");
      const existingImage = editCategory.image || "";
      setImage(existingImage);
      setUploadedImageUrl(existingImage);
      setImageFile(null);
      setFileName("");
      setErrors({});
    } else {
      // Reset for add mode
      setImage("");
      setImageFile(null);
      setUploadedImageUrl("");
      setFileName("");
      setName("");
      setErrors({});
    }
  }, [open, editCategory]);

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = (event.target as HTMLInputElement).files;
    if (files && files[0]) {
      const file = files[0];
      
      // Validate file type
      if (!file.type.includes("image")) {
        setErrors((prev) => ({ ...prev, image: "Please select a valid image file" }));
        return;
      }

      // Validate file size (e.g., max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({ ...prev, image: "Image size should be less than 5MB" }));
        return;
      }

      setImageFile(file);
      setFileName(file.name);
      setImage(URL.createObjectURL(file));
      // Clear image error when user selects a new image
      if (errors.image) {
        setErrors((prev) => ({ ...prev, image: undefined }));
      }
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setName(newName);
    // Clear name error when user types
    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  // Validate form fields
  const validateForm = (): boolean => {
    const newErrors: { name?: string; image?: string } = {};

    // Validate name
    if (!name.trim()) {
      newErrors.name = "Category name is required";
    } else if (name.trim().length < 2) {
      newErrors.name = "Category name must be at least 2 characters";
    } else if (name.trim().length > 50) {
      newErrors.name = "Category name must be less than 50 characters";
    }

    // Validate image - required for add mode, optional for edit mode (if already has image)
    if (!isEditMode && !imageFile) {
      newErrors.image = "Please upload an image";
    } else if (isEditMode && !imageFile && !uploadedImageUrl) {
      newErrors.image = "Please upload an image";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      showError("Please fix the errors in the form");
      return;
    }

    try {
      let imageUrl = uploadedImageUrl;

      // Step 1: Upload image if a new file is selected
      if (imageFile) {
        const uploadResult = await uploadFile({ file: imageFile }).unwrap();
        
        // Extract image URL from upload response
        imageUrl = uploadResult.data?.data || uploadResult.data;
        
        if (!imageUrl) {
          showError("Failed to get image URL from server.");
          return;
        }
      }

      // If no image URL available, show error
      if (!imageUrl) {
        showError("Please upload an image");
        return;
      }

      // Log payload for debugging
      const payload = {
        name: name.trim(),
        image: imageUrl,
      };
      console.log("🚀 Category Payload:", payload);

      // Step 2: Create or update category with image URL
      if (isEditMode && editCategory?._id) {
        // Update existing category
        const updateResponse = await updateCategory({
          id: editCategory._id,
          name: name.trim(),
          image: imageUrl,
        }).unwrap();

        // Show success message from API response or default message
        const successMessage =
          updateResponse?.message || `Category "${name.trim()}" updated successfully`;
        showToast(successMessage);
      } else {
        // Create new category
        const createResponse = await createCategory({
          name: name.trim(),
          image: imageUrl,
        }).unwrap();

        // Show success message from API response or default message
        const successMessage =
          createResponse?.message || `Category "${name.trim()}" created successfully`;
        showToast(successMessage);
      }

      // Reset form and close modal
      setImage("");
      setImageFile(null);
      setUploadedImageUrl("");
      setFileName("");
      setName("");
      setErrors({});
      setOpen(false);
    } catch (error: any) {
      console.error(`Error ${isEditMode ? "updating" : "creating"} category:`, error);

      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        `Failed to ${isEditMode ? "update" : "create"} category. Please try again.`;
      showError(errorMessage);
    }
  };

  return (
    <>
      <Loader isLoad={isLoading} />
      <Modal
        className="modal rejectReason_modal"
        open={open}
        onClose={onClose}
      >
        <div className="modal-dialog">
          <div className="modal-body">
            <div className="modal_title">
              <h2>{isEditMode ? "Edit Product Category" : "Add Product Category"}</h2>
              <button
                type="button"
                className="btn-close"
                onClick={() => setOpen(false)}
                aria-label="Close"
                disabled={isLoading}
              >
                <CloseIcon />
              </button>
            </div>

            <form className="form" onSubmit={handleSubmit}>
              <div className="control_group w_50">
                <label htmlFor="icon-button-file">
                  Upload Image<span className="req">*</span>
                </label>
                {image ? (
                  <div className="upload_image">
                    <div className="upload_image_holder no_radius v2">
                      <figure>
                        <CardMedia component="img" image={image} alt="photo" />
                      </figure>
                      <CloseIcon
                        onClick={() => {
                          setImage("");
                          setImageFile(null);
                          setUploadedImageUrl("");
                          setFileName("");
                          if (errors.image) {
                            setErrors((prev) => ({ ...prev, image: undefined }));
                          }
                        }}
                        style={{ cursor: "pointer" }}
                      />
                    </div>
                  </div>
                ) : (
                  <label className="upload_image" htmlFor="icon-button-file">
                    <Input
                      sx={{ display: "none" }}
                      id="icon-button-file"
                      type="file"
                      inputProps={{ accept: "image/png,image/jpeg" }}
                      onChange={handleImageChange}
                    />
                    <span className="upload_image_holder no_radius">
                      <figure className="add_img">
                        <img src="/static/images/placeholder.png" alt="icon" />
                        <span className="upload_img_icon">
                          <img src="/static/images/edit2_icon.svg" alt="icon" />
                        </span>
                      </figure>
                    </span>
                  </label>
                )}
                {errors.image && (
                  <FormHelperText error sx={{ marginTop: "4px" }}>
                    {errors.image}
                  </FormHelperText>
                )}
              </div>

              <div className="control_group">
                <label htmlFor="category-name">
                  Category Name<span className="req">*</span>
                </label>
                <TextField
                  id="category-name"
                  placeholder="Enter Category Name"
                  fullWidth
                  hiddenLabel
                  value={name}
                  onChange={handleNameChange}
                  error={!!errors.name}
                  disabled={isLoading}
                />
                {errors.name && (
                  <FormHelperText error sx={{ marginTop: "4px" }}>
                    {errors.name}
                  </FormHelperText>
                )}
              </div>

              <div className="footer_btn_flex">
                <Button
                  className="btn btn_grey"
                  onClick={() => setOpen(false)}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  className="btn btn_primary"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? (isEditMode ? "Updating..." : "Creating...") : "Save"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </Modal>
    </>
  );
}
